/**
 * SpecialistID — Product PDP modular bundle: VOLUME PRICING
 * Part of the custom-product-* set (split out of the legacy custom-product.js).
 *
 * Owns the Regios volume-pricing table: populating the Price column, highlighting
 * the current-quantity row, the discount progress bar, table padding adjustment,
 * and the MutationObserver that initialises the table once Regios renders it.
 *
 * Public API: SPID.updateTablePrice()
 * Private: observeTableChange, discountProgressBar, extractRanges,
 *          findRangeForQuantity, adjustTablePadding.
 *
 * LOAD ORDER: after custom-product-core.js + custom-product-data.js +
 * custom-product-inventory.js + custom-product-pricing.js +
 * custom-product-quantity.js, before custom-product.js.
 */
(function () {
  'use strict';

  const SPID = window.SPID;
  const CONFIG = SPID.config;
  const debugLog = SPID.debugLog;
  const getVariantId = SPID.getVariantId;
  const getVariantData = SPID.getVariantData;
  const getVariantPrice = SPID.getVariantPrice;
  const getVariantMetafield = SPID.getVariantMetafield;
  const getProductMetafield = SPID.getProductMetafield;
  // Shared MAP/MAPP guard, defined in custom-product-pricing.js (loads earlier).
  // The tier table and the buy box must reach the same verdict, so both call this.
  const isPriceHidden = SPID.isPriceHidden;

  function observeTableChange() {
    const tableNode = document.getElementById('regios-dopp-volume-pricing-table-target');
    if (!tableNode) return;

    const config = {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    };

    const callback = function(mutationsList, observer) {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          observer.disconnect();
        }
      }
    };

    const observer = new MutationObserver(callback);
    observer.observe(tableNode, config);
  }

  function discountProgressBar() {
    const progressEl = document.querySelector("#discount-progress");
    if (progressEl) progressEl.classList.remove("hidden");

    const quantityInput = document.querySelector('.quantity__input');

    function updateProgress(selectedQty) {
      // Read the tiers fresh on every update instead of closing over a snapshot.
      //
      // Two things went wrong with a captured list. A variant swap replaces the
      // table, so the snapshot goes stale. And an empty list fell straight
      // through to the "no next slab" branch below, which fills the bar to 100%
      // and writes "Max Discount Reached" — not because the shopper is past the
      // top tier, but because there were no tiers to compare against. That was
      // the flicker on every variant switch: the bar filled and claimed maximum
      // discount, then corrected itself once the table re-rendered.
      //
      // No tier data means we know nothing, so leave the bar alone rather than
      // asserting the most optimistic possible state.
      const table = document.querySelector(".regios-dopp-generic-volume-pricing-table");
      if (!table) return;
      const slabs = extractRanges(table);
      if (!slabs.length) return;

      let currentDiscount = 0;
      let nextSlab = null;

      for (let i = 0; i < slabs.length; i++) {
        if (selectedQty >= slabs[i].start) {
          currentDiscount = slabs[i].perct;
        } else {
          nextSlab = slabs[i];
          break;
        }
      }

      if (isNaN(currentDiscount)) {
        currentDiscount = 0;
      }

      const currentDiscountEl = document.querySelector('#current-discount');
      if (currentDiscountEl) currentDiscountEl.textContent = `Current Discount: ${currentDiscount}%`;

      if (nextSlab) {
        const progress = ((selectedQty / nextSlab.start) * 100).toFixed(1);
        const discountBar = document.querySelector('#discount-bar');
        if (discountBar) discountBar.style.width = `${Math.min(progress, 100)}%`;
        const nextDiscountInfo = document.querySelector('#next-discount-info');
        if (nextDiscountInfo) nextDiscountInfo.textContent = `Add ${nextSlab.start - selectedQty} more to save ${nextSlab.perct}%!`;
      } else {
        const discountBar = document.querySelector('#discount-bar');
        if (discountBar) discountBar.style.width = '100%';
        const nextDiscountInfo = document.querySelector('#next-discount-info');
        if (nextDiscountInfo) nextDiscountInfo.textContent = 'Max Discount Reached';
      }
    }

    if (quantityInput) {
      // Bind once. discountProgressBar() runs on every updateTablePrice, so
      // without this guard each variant switch stacked another input listener,
      // and every one of them fired on a single keystroke. Harmless in itself,
      // but it multiplied the stale-closure problem above.
      if (!quantityInput.dataset.spidProgressBound) {
        quantityInput.addEventListener('input', function () {
          const qty = parseInt(this.value) || 0;
          updateProgress(qty);
        });
        quantityInput.dataset.spidProgressBound = '1';
      }

      const initialQty = parseInt(quantityInput.value) || 0;
      updateProgress(initialQty);
    }
  }

  // Wrapper so the variant-swap table skeleton clears the moment pricing has
  // actually run, rather than on a fixed delay from the option click.
  //
  // The clear sits in a finally because updateTablePriceInner has five early
  // returns — no table, no product data, MAP-hidden price and so on. Clearing
  // only on the success path would leave the skeleton sitting over the volume
  // table until the 4s safety timeout on every one of those.
  function updateTablePrice() {
    try {
      updateTablePriceInner();
    } finally {
      // Defined in custom-product-variant-switch.js, which loads after this
      // file, so it is resolved at call time rather than captured at load.
      if (SPID.endVariantTableLoading) SPID.endVariantTableLoading();
    }
  }

  function updateTablePriceInner() {
    // Shopify-consistent rounding: round the discount DOWN, then subtract.
    // Done in whole cents: on dollars the float lands a hair under an exact
    // cent often enough to matter (1.15 * 0.20 * 100 is 22.999999999999996,
    // which would floor a cent low). Percentages carry at most two decimals
    // and prices are whole cents, so nothing legitimate sits within the
    // epsilon of an integer.
    function shopifyDiscountPrice(price, discountPercent) {
      const priceInCents = Math.round(price * 100);
      const discountInCents = Math.floor(priceInCents * discountPercent + 1e-9);
      return (priceInCents - discountInCents) / 100;
    }

    document.querySelectorAll(".volume_pricing_message").forEach(el => el.classList.add("hidden"));
    document.querySelectorAll(".bulk-ordering-message").forEach(el => el.classList.add("hidden"));
    document.querySelectorAll(".volume_pricing_info").forEach(el => el.classList.add("hidden"));
  
    const table = document.querySelector(".regios-dopp-generic-volume-pricing-table");
    if (!table) {
      const progressEl = document.querySelector("#discount-progress");
      if (progressEl) progressEl.classList.add("hidden");
      return;
    }

    discountProgressBar();
    
    const variant_id = getVariantId();
    if (!variant_id) {
      console.error("Variant not found.");
      return;
    }
    
    const variant = getVariantData(variant_id);
    if (!variant) {
      console.error("Variant data not found for ID:", variant_id);
      return;
    }

    const originalPriceElement = document.querySelector(".actual-price")?.querySelector(".product-price");
    if (!originalPriceElement) {
      console.error("Original price element not found.");
      return;
    }

    // Get price - check FPD price first for FPD products
    let originalPrice = 0;
    if (document.body.classList.contains('fpd-enabled')) {
      const fpdPriceEl = document.querySelector(".variant_price");
      if (fpdPriceEl && fpdPriceEl.value) {
        originalPrice = parseFloat(fpdPriceEl.value);
      }
    }
    // Fall back to variant price if FPD price not available
    if (!originalPrice || originalPrice === 0) {
      originalPrice = getVariantPrice(variant_id);
    }

    /* MAP / MAPP: when the price may not be printed, the volume pricing table
       must not print it either -- and it must not overwrite the "Add to Cart for
       Price" line that mapMsrpLogic() just wrote into .actual-price. Keep the
       quantity/discount columns (those advertise no price) and blank the price
       column. See isPriceHidden() for the rule. */
    const mapHidesPrice = isPriceHidden(variant_id, originalPrice);
    if (mapHidesPrice) {
      const thead0 = table.querySelector("thead tr");
      let priceCol = -1;
      thead0?.querySelectorAll('th').forEach((th, i) => {
        if (th.textContent.trim().toLowerCase() === 'price') priceCol = i;
      });
      if (priceCol > -1) {
        thead0.querySelectorAll('th')[priceCol].remove();
        table.querySelectorAll('tbody tr').forEach(function (r) {
          const c = r.querySelectorAll('td');
          if (c[priceCol]) c[priceCol].remove();
        });
      }
      const totalWrap = document.querySelector("div.total-price-wrapper");
      if (totalWrap) totalWrap.classList.add("hidden");
      document.querySelectorAll(".bulk-ordering-message").forEach(el => el.classList.remove("hidden"));
      document.querySelectorAll(".volume_pricing_info").forEach(el => el.classList.remove("hidden"));
      adjustTablePadding();
      return;
    }

    // Don't exit if price is 0 - it might be a valid price or just not loaded yet
    const currentQuantity = parseFloat(document.querySelector(".quantity__input")?.value) || 1;
    // Get minimum quantity with same priority logic as optionLogic
    const minQuantity = parseInt(getVariantMetafield(variant_id, 'c_f', 'minimum')) ||
                        parseInt(getVariantMetafield(variant_id, 'inventory', 'minimum')) ||
                        parseInt(getProductMetafield('c_f', 'minimum')) ||
                        parseInt(getProductMetafield('inventory', 'minimum')) || 1;
    const ranges = extractRanges(table);
    const currentRange = findRangeForQuantity(currentQuantity, ranges);

    // Use Shopify currency helpers if available
    const formatCurrencyLocal = (value) => {
      if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
        return Shopify.formatMoney(value * 100); // Shopify expects price in cents
      }
      const discountedPrice = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
      return `$${discountedPrice}`; // Fallback to basic formatting
    };

    const body = document.querySelector("body");
    const bodyHasPrice = body?.classList.contains("product.price-discount");
    debugLog('BodyHasPrice: ' + bodyHasPrice);

    // Check if the "Price" column already exists
    const thead = table.querySelector("thead tr");
    const priceColumnExists = Array.from(thead.querySelectorAll("th")).some(
      th => th.innerText.trim().toLowerCase() === "price"
    );

    let priceColumnIndex = -1;
    thead.querySelectorAll('th').forEach((th, index) => {
      if (th.textContent.trim().toLowerCase() === 'price') {
        priceColumnIndex = index;
      }
    });

    // Update rows with calculated prices
    const tbody = table.querySelector("tbody");
    
    // Check if first row already has "1+" quantity
    const priceCellText = `${minQuantity > 1 ? minQuantity : 1}+`;
    const firstRow = tbody.querySelector("tr:first-child");
    const firstQty = firstRow?.querySelector("td:first-child")?.innerText.trim();
    const hasOnePlusRow = firstRow && (firstQty === priceCellText || minQuantity > +firstQty.replace(/[^0-9.]/g, ''));
    
    if (!hasOnePlusRow) {
      // Create the "1+" row
      const onePlusRow = document.createElement("tr");
      
      // Quantity column
      const quantityCell = document.createElement("td");
      quantityCell.innerText = priceCellText;
      onePlusRow.appendChild(quantityCell);
      
      // Discount column
      const discountCell = document.createElement("td");
      discountCell.innerText = bodyHasPrice ? formatCurrencyLocal(originalPrice) : "NA";
      onePlusRow.appendChild(discountCell);
      
      // Price column (empty if price column exists, or will be added later)
      if (priceColumnExists) {
        const priceCell = document.createElement("td");
        priceCell.innerText = ""; // Empty text
        onePlusRow.appendChild(priceCell);
      }
      
      // Insert at the beginning of tbody
      tbody.insertBefore(onePlusRow, tbody.firstChild);
    }

    const rows = tbody.querySelectorAll("tr");
    const rewardPointsEl = document.querySelector('div.reward_points > span');

    rows.forEach((row) => {
      const cells = row.querySelectorAll("td");
      const quantity = parseFloat(cells[0].innerText.replace(/[^0-9.]/g, ''));
      const discountText = cells[1].innerText;
      var discountPercent = parseFloat(discountText.replace(/[^0-9.]/g, '')) / 100;
      const discountPrice = parseFloat(discountText.replace(/[^0-9.]/g, ''));

      // Price this row off the tier's real percentage, not the rounded one the
      // app printed in the Discount cell. "6% off" is a 6.25% tier displayed
      // through Math.round(), and pricing the label instead of the tier quotes
      // $0.76 where checkout charges $0.75. The label itself is left alone.
      const exactPercent = window.SPID_VolumeTiers
        ? window.SPID_VolumeTiers.percentForQuantity(quantity)
        : null;
      if (exactPercent !== null && isFinite(exactPercent)) {
        discountPercent = exactPercent / 100;
      }

      // Some ladders name the price outright instead of a discount - the
      // visitor badge log book is priced that way. There is no percentage to
      // correct in that case, so take the tier's own price and skip the
      // percentage maths entirely; the rounded "6% off" label stays as the app
      // rendered it, the same as for percentage tiers.
      const exactPrice = window.SPID_VolumeTiers && window.SPID_VolumeTiers.priceForQuantity
        ? window.SPID_VolumeTiers.priceForQuantity(quantity)
        : null;

      if (minQuantity > quantity) {
        row.classList.add("hidden");
      } else {
        row.classList.remove("hidden");
      }

      if (currentRange && quantity === currentRange.start) {
        row.classList.add("current");
      } else {
        row.classList.remove("current");
      }

      if (isNaN(discountPercent)) {
        discountPercent = 0;
      }

      if (!isNaN(discountPercent)) {
        // USE SHOPIFY-CONSISTENT ROUNDING HERE
        const discountedPrice = (exactPrice !== null && isFinite(exactPrice))
          ? exactPrice
          : (bodyHasPrice ? discountPrice : shopifyDiscountPrice(originalPrice, discountPercent));
        const discountedPriceRounded = Math.round(discountedPrice * 100) / 100;

        if (!bodyHasPrice && !priceColumnExists) {
          const priceCell = document.createElement("td");
          priceCell.innerText = formatCurrencyLocal(discountedPriceRounded) + " each";
          row.appendChild(priceCell);
        } else {
          const cellsList = row.querySelectorAll('td');
          if (priceColumnIndex > -1 && cellsList[priceColumnIndex]) {
            cellsList[priceColumnIndex].innerText = formatCurrencyLocal(discountedPriceRounded) + " each";
          }
        }

        if (currentRange && quantity === currentRange.start) {
          originalPriceElement.innerHTML = formatCurrencyLocal(discountedPriceRounded) + '<span class="price-ea">ea</span>';
          
          const discountedEstimatedTotal = discountedPriceRounded * currentQuantity;
          const totalPriceEl = document.querySelector(".total-price-wrapper .total-price");
          if (totalPriceEl) totalPriceEl.innerHTML = formatCurrencyLocal(discountedEstimatedTotal);
          if (rewardPointsEl) rewardPointsEl.innerHTML = discountedEstimatedTotal.toFixed(2);
        }
      }
    });

    let currentRowExists = document.querySelector(".regios-dopp-generic-volume-pricing-table tbody tr.current");
    if (!currentRowExists) {
      originalPriceElement.innerHTML = formatCurrencyLocal(originalPrice) + '<span class="price-ea">ea</span>';
    }

    document.querySelectorAll(".bulk-ordering-message").forEach(el => el.classList.remove("hidden"));
    document.querySelectorAll(".volume_pricing_info").forEach(el => el.classList.remove("hidden"));

    adjustTablePadding();

    const maxDiscountEl = document.querySelector('.regios-dopp-generic-volume-pricing-table tbody tr:last-child td:nth-child(2)');
    var maxDiscountPerct = maxDiscountEl?.textContent || '';
    const maxPercentEl = document.querySelector(".max_percent");
    if (maxPercentEl) maxPercentEl.innerHTML = maxDiscountPerct;

    // Update header to include "Price"
    if (!bodyHasPrice && thead && thead.childElementCount === 2) {
      const priceHeader = document.createElement("th");
      priceHeader.scope = "col";
      priceHeader.innerText = "Price";
      thead.appendChild(priceHeader);
    }
  }

  function extractRanges(table) {
    const ranges = [];
    const rows = table.querySelectorAll("tbody tr");
    const body = document.querySelector("body");
    const bodyHasPrice = body?.classList.contains("product.price-discount") || body?.classList.contains("product price-discount");
    const variant_id = getVariantId();
    const originalPrice = getVariantPrice(variant_id);

    rows.forEach((row) => {
      const quantityCell = row.querySelector("td:first-child");
      const discountPerct = row.querySelector("td:nth-child(2)");
      if (quantityCell) {
        const rangeText = quantityCell.innerText.trim();
        const match = rangeText.match(/^(\d+)\+?$/);
        if (match) {
          let perct = 0;
          if (discountPerct) {
            if (bodyHasPrice && originalPrice > 0) {
              const discountPrice = parseFloat(discountPerct.innerText.trim().replace("$", ""));
              if (!isNaN(discountPrice)) {
                perct = parseInt((originalPrice - discountPrice) / originalPrice * 100);
              }
            } else if (!bodyHasPrice) {
              const percentText = discountPerct.innerText.trim().replace("% off", "").replace("%", "");
              const parsedPerct = parseInt(percentText);
              if (!isNaN(parsedPerct)) {
                perct = parsedPerct;
              }
            }
          }

          const start = parseInt(match[1], 10);
          ranges.push({ start, perct });
        }
      }
    });

    for (let i = 0; i < ranges.length; i++) {
      ranges[i].end = i < ranges.length - 1 ? ranges[i + 1].start - 1 : null;
    }

    return ranges;
  }

  function findRangeForQuantity(quantity, ranges) {
    if (isNaN(quantity)) {
      console.error("Invalid quantity provided.");
      return null;
    }

    for (let i = 0; i < ranges.length; i++) {
      const { start, end } = ranges[i];
      if (quantity >= start && (end === null || quantity <= end)) {
        return ranges[i];
      }
    }

    return null;
  }

  function adjustTablePadding() {
    let totalHiddenHeight = 0;
    document.querySelectorAll(".regios-dopp-generic-volume-pricing-table tbody tr.hidden").forEach(el => {
      totalHiddenHeight += CONFIG.HIDDEN_ROW_HEIGHT;
    });
    const bulkMessage = document.querySelector(".bulk-ordering-message");
    if (bulkMessage) bulkMessage.style.paddingBottom = totalHiddenHeight + "px";
  }

  SPID.updateTablePrice = updateTablePrice;

  // Watch for Regios to render the table, then initialise volume pricing.
  function watchForVolumeTable() {
    // A table already present (e.g. this file re-evaluated after a Turbo swap
    // that happened to be slower than the app) still needs pricing.
    const existing = document.querySelector(".regios-dopp-generic-volume-pricing-table");
    if (existing) {
      observeTableChange();
      updateTablePrice();
      return;
    }

    const observer = new MutationObserver((mutationsList, observer) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const table = document.querySelector(".regios-dopp-generic-volume-pricing-table");
          if (table) {
            observer.disconnect();
            debugLog('Table changed!');
            observeTableChange();
            updateTablePrice();
          }
        }
      }
    });

    const targetNode = document.body;
    observer.observe(targetNode, { childList: true, subtree: true });
  }

  // Must not be gated on DOMContentLoaded alone. Turbo Drive re-evaluates this
  // file on a soft navigation but does NOT re-fire DOMContentLoaded, so the
  // observer was never re-registered after a body swap. The Regios app does not
  // re-render its table on a soft nav either, so custom-product-volume-fallback
  // builds one ~1.8s later — and with nothing watching, that table was never
  // priced. Shoppers saw discount percentages with no per-unit prices and no
  // "1+" base row, which is exactly the column the SPID-169 tier fix lives in.
  if (document.readyState === 'loading') {
    document.addEventListener("DOMContentLoaded", watchForVolumeTable);
  } else {
    watchForVolumeTable();
  }

  // Announce this module so main-product.liquid can leave it out safely.
  if (window.SPID && SPID.register) SPID.register('volume-pricing');
})();
